import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  Save,
  X,
  AlertTriangle,
  Camera,
  ChevronDown,
  PlusCircle,
} from "lucide-react";
import {
  addDoc,
  collection,
  doc,
  updateDoc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  db,
  getAuthors,
  getBlogCategories,
  storage,
} from "../../services/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { MarkdownField } from "../ui/MarkDown";
import { query, where, getDocs, Timestamp } from "firebase/firestore";
import { AuthorType, BlogCategoryType } from "../../types/blog";
import AddBlogAuthorModal from "./AddBlogAuthorModal";
import { validateImageFile } from "../utils/imageValidation";
import { cropImageFile } from "../utils/cropImage";
import { resizeImage } from "../utils/resizeImage";
import Cropper from "react-easy-crop";

// Helper function to get Eastern Time
export const getEasternDateTime = () => {
  return new Date()
    .toLocaleString("sv-SE", {
      timeZone: "America/New_York",
    })
    .replace(" ", "T")
    .slice(0, 16);
};

// Types and interfaces

interface BlogProps {
  blogId?: string;
  isEditable?: boolean;
  onSaveSuccess?: () => void;
  setIsEdit?: (value: boolean) => void;
  isEdit?: boolean;
}

const Blog: React.FC<BlogProps> = ({
  blogId,
  onSaveSuccess,
  setIsEdit,
  isEdit,
}) => {
  // State management
  const [title, setTitle] = useState("");
  const [readingTime, setReadingTime] = useState(0);
  const [dateTimeInEasternTime, setDateTimeInEasternTime] = useState<
    string | number | readonly string[] | undefined
  >(getEasternDateTime());
  const [content, setContent] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [slug, setSlug] = useState("");
  const [authors, setAuthors] = useState<AuthorType[]>([]);
  const [blogCategories, setBlogCategories] = useState<BlogCategoryType[]>([]); // Placeholder for blog categories>
  const [selectedAuthorId, setSelectedAuthorId] = useState("");
  const [selectedBlogCategoryId, setSelectedBlogCategoryId] = useState<
    number | undefined
  >(1);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<any>(null);
  const [crop, setCrop] = useState({ x: 16, y: 9 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<any>(null);
  const [loadingCropper, setLoadingCropper] = useState(false);
  const [selectedAspectRatioType, setSelectedAspectRatioType] =
    useState("landscape");
  const [imageMaxHeight, setImageMaxHeight] = useState<number | null | undefined>(null);

  const authorOptions = useMemo(() => {
    return authors.map((author) => ({
      id: author.id,
      name: author.name,
      image: author.image,
      thumbnail: author.thumbnail,
    }));
  }, [authors]);

  useEffect(() => {
    const fetchBlogData = async () => {
      if (!blogId) return;

      try {
        const blogDoc = await getDoc(doc(db, "blog", blogId));
        if (blogDoc.exists()) {
          const data = blogDoc.data();
          setSelectedImage(data.image);
          setImagePreview(data.image);
          setTitle(data.title || "");
          setDateTimeInEasternTime(
            data.dateTimeInEasternTime || getEasternDateTime()
          );
          setReadingTime(data.readingTime || 0);
          setSelectedBlogCategoryId(data.blogCategoryId);
          setContent(data.content || "");
          setExcerpt(data.excerpt || "");
          setSlug(data.slug || "");
          setSelectedAuthorId(data.authorId || "");
          setImageMaxHeight(data.imageMaxHeight || null);
        }
      } catch (error) {
        console.error("Error fetching blog:", error);
        setError("Failed to load blog data");
      }
    };

    if (blogId) {
      fetchBlogData();
    }
  }, [blogId]);

  const fetchAuthors = async () => {
    try {
      const authorsList = await getAuthors();
      setAuthors(authorsList);
    } catch (error) {
      console.error("Failed to fetch authors:", error);
    }
  };

  useEffect(() => {
    fetchAuthors();
  }, []);

  const fetchBlogCategories = async () => {
    try {
      const blogCategoryList = await getBlogCategories();
      //sorting as per display order
      blogCategoryList.sort(
        (a, b) => a.blog_category_display_order - b.blog_category_display_order
      );
      setBlogCategories(blogCategoryList);
    } catch (error) {
      console.error("Failed to fetch blog categories:", error);
    }
  };
  useEffect(() => {
    fetchBlogCategories();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Validation
  const validateChallenge = async () => {
    if (!title.trim()) {
      setError("Title is required");
      return false;
    }

    if (!slug.trim()) {
      setError("Slug is required");
      return false;
    }

    if (slug.trim() !== "") {
      const checkDuplicate: any = query(
        collection(db, "blog"),
        where("slug", "==", slug)
      );

      const conflictsSnapshot = await getDocs(checkDuplicate);

      // Get all documents' data as an array of objects
      const conflicts = conflictsSnapshot.docs.map((doc) => ({
        id: doc.id,
      }));
      if (!blogId) {
        if (conflicts.length > 0) {
          setError("Slug is already taken");
          return false;
        }
      } else {
        if (conflicts.length > 0 && conflicts[0]["id"] !== blogId) {
          setError("Slug is already taken");
          return false;
        }
      }
    }

    if (!selectedAuthorId.trim()) {
      setError("Author is required");
      return false;
    }

    if (!content.trim()) {
      setError("Content is required");
      return false;
    }

    setError(null);
    return true;
  };

  const resetImageState = useCallback(() => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const handleImageChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (fileInputRef.current) fileInputRef.current.value = "";

      const validationError = validateImageFile(file);
      if (validationError) {
        alert(validationError);
        setError(validationError);
        return;
      }

      setLoadingCropper(true);
      try {
        const { resizedUrl: resized } = await resizeImage(file);
        setTempImageUrl(resized);
        setShowCropper(true);
        setError(null);
      } finally {
        setLoadingCropper(false);
      }
    },
    []
  );

  const onCropComplete = useCallback((_: any, croppedPixels: any) => {
    setCroppedArea(croppedPixels);
  }, []);

  const handleCropConfirm = useCallback(async () => {
    if (!tempImageUrl || !croppedArea) return;

    const cropped = await cropImageFile(tempImageUrl, croppedArea);
    if (cropped) {
      setSelectedImage(cropped);
      setImagePreview(URL.createObjectURL(cropped));
    }

    setShowCropper(false);
  }, [tempImageUrl, croppedArea]);

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const generateSlug = (title: string) => {
    let slug = "";
    if (title) {
      slug = title
        .normalize("NFD") // split accents from letters
        .replace(/[\u0300-\u036f]/g, "") // remove accent marks
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-") // collapse non-alphanumerics to hyphens
        .replace(/^-+|-+$/g, ""); // remove leading/trailing hyphens
    }
    setSlug(slug);
  };

  // Save functionality
  const handleSave = async () => {
    if (!(await validateChallenge())) {
      return;
    }

    setIsSaving(true);
    try {
      // Upload image and get URL if image exists
      let imageUrl = null;
      if (selectedImage && selectedImage.name) {
        const storageRef = ref(
          storage,
          `blog/${Date.now()}_${selectedImage.name}`
        );
        await uploadBytes(storageRef, selectedImage);
        imageUrl = await getDownloadURL(storageRef);
      } else {
        imageUrl = selectedImage ? selectedImage : null;
      }

      let selectedAuthorName = authors.find(
        (a) => a.id === selectedAuthorId
      )?.name;
      let selectedAuthorThumbnail =
        authors.find((a) => a.id === selectedAuthorId)?.thumbnail ||
        `https://ui-avatars.com/api/?name=${encodeURIComponent(
          authors.find((a) => a.id === selectedAuthorId)?.name || "Author"
        )}&background=random&size=32`;

      const blogData = {
        title: title.trim(),
        readingTime: readingTime,
        blogCategoryId: selectedBlogCategoryId,
        content: content.trim(),
        excerpt: excerpt.trim(),
        image: imageUrl,
        slug: slug,
        authorId: selectedAuthorId,
        author: selectedAuthorName,
        thumbnail: selectedAuthorThumbnail,
        updatedAt: serverTimestamp(),
        dateTimeInEasternTime: dateTimeInEasternTime,
        imageMaxHeight: imageMaxHeight ? imageMaxHeight : null
      };

      if (blogId) {
        await updateDoc(doc(db, "blog", blogId), blogData);
        isEdit ? setIsEdit?.(false) : setIsEdit?.(true);
      } else {
        await addDoc(collection(db, "blog"), {
          ...blogData,
          createdAt: serverTimestamp(),
        });
      }

      // Clear form and image state after successful save
      if (!blogId) {
        setTitle("");
        setDateTimeInEasternTime(getEasternDateTime());
        setReadingTime(0);
        setSelectedBlogCategoryId(1);
        setContent("");
        setExcerpt("");
        setSelectedImage(null);
        setImagePreview(null);
        setSlug("");
        setSelectedAuthorId("");
        setImageMaxHeight(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }

      if (onSaveSuccess) {
        onSaveSuccess();
      }

      setError(null);
    } catch (error) {
      console.error("Error saving blog:", error);
      setError("Failed to save blog. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const imageSection = useMemo(
    () => (
      <div className="mt-2 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md">
        <div className="space-y-2 text-center">
          <div className="flex flex-col items-center">
            {imagePreview ? (
              <div className="relative">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="max-h-48 rounded-lg"
                />
                <button
                  type="button"
                  onClick={resetImageState}
                  className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <Camera size={48} className="mx-auto text-gray-400" />
            )}
          </div>
          <label
            htmlFor="image-blog-upload"
            className="cursor-pointer text-[#18f7b1] hover:text-[#0fd494]"
          >
            {imagePreview ? "Change image" : "Upload an image"}
            <input
              id="image-blog-upload"
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="sr-only"
            />
          </label>
          <p className="text-sm text-gray-500">
            PNG, JPG, JPEG, WEBP up to 10MB
          </p>
        </div>
      </div>
    ),
    [imagePreview, handleImageChange, resetImageState]
  );

  const handleAspectRatioChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedAspectRatioType(e.target.value);
    const aspectType = e.target.value;
    if (aspectType === "portrait") {
      setCrop({ x: 9, y: 16 });
    } else if (aspectType === "landscape") {
      setCrop({ x: 16, y: 9 });
    }
    resetImageState();
  };

  const getAspectRatio = () => {
    if (selectedAspectRatioType === "portrait") {
      return 9 / 16;
    } else if (selectedAspectRatioType === "landscape") {
      return 16 / 9;
    } else if (selectedAspectRatioType === "square") {
      return 1;
    }
  };
  const handleBlogCategoryChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedBlogCategoryId(+e.target.value);
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      {/* Blog Info Section */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <h2 className="text-2xl font-bold mb-4">Article Information</h2>

        {error && (
          <div className="flex items-center gap-2 bg-red-50 border-l-4 border-red-500 p-4 mb-4">
            <AlertTriangle className="text-red-500" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium mb-2">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                generateSlug(e.target.value);
              }}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[#18f7b1]"
              placeholder="Enter title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Slug</label>
            <input
              type="text"
              value={slug}
              onChange={(e) => {
                generateSlug(e.target.value);
              }}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[#18f7b1]"
              placeholder="Enter slug"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Article Image</label>
            <select
              value={selectedAspectRatioType}
              onChange={handleAspectRatioChange}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[#18f7b1]"
              defaultValue={selectedAspectRatioType}
            >
              <option value="landscape">Landscape (16:9)</option>
              <option value="portrait">Portrait (9:16)</option>
              <option value="square">Square (1:1)</option>
            </select>
            {imageSection}
          </div>
          {loadingCropper && (
            <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center">
              <div className="text-white text-xl flex items-center gap-4">
                <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-white border-opacity-70" />
                Preparing image for cropping...
              </div>
            </div>
          )}
          {showCropper && tempImageUrl && (
            <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
              <div className="relative w-[90vw] max-w-2xl h-[90vh] bg-white rounded-lg overflow-hidden shadow-lg">
                <Cropper
                  image={tempImageUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={getAspectRatio()}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
                <div className="absolute bottom-0 left-0 w-full flex justify-between p-4 bg-white border-t">
                  <button
                    onClick={() => setShowCropper(false)}
                    className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCropConfirm}
                    className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                  >
                    Crop & Save
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="relative">
            <label className="block text-sm font-medium mb-2">
              Article Image Max Height (Leave blank to load default)
            </label>
            <input
              type="number"
              value={imageMaxHeight || ''}
              onChange={(e) => {
                setImageMaxHeight(parseInt(e.target.value));
              }}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[#18f7b1]"
              placeholder="250"
            />
          </div>
          {/* Author Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <label className="block text-sm font-medium mb-2">Author</label>

            <div className="flex">
              <button
                onClick={() => setDropdownOpen((prev) => !prev)}
                type="button"
                className="w-full flex items-center justify-between p-2 border rounded-md focus:ring-2 focus:ring-[#18f7b1] bg-white"
              >
                {selectedAuthorId ? (
                  <div className="flex items-center gap-2">
                    <img
                      src={
                        authors.find((a) => a.id === selectedAuthorId)
                          ?.thumbnail ||
                        `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          authors.find((a) => a.id === selectedAuthorId)
                            ?.name || "Author"
                        )}&background=random&size=32`
                      }
                      alt="Selected Author"
                      className="w-6 h-6 rounded-full object-cover"
                    />
                    <span>
                      {authors.find((a) => a.id === selectedAuthorId)?.name ||
                        "Select Author"}
                    </span>
                  </div>
                ) : (
                  <span className="text-gray-500">Select an author</span>
                )}
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </button>
              <AddBlogAuthorModal isAdmin={true} onAuthorAdded={fetchAuthors} />
            </div>
            {dropdownOpen && (
              <ul className="absolute z-10 mt-1 w-full bg-white border rounded-md shadow-md max-h-60 overflow-auto">
                {authorOptions.map((author) => (
                  <li
                    key={author.id}
                    onClick={() => {
                      setSelectedAuthorId(author.id);
                      setDropdownOpen(false);
                    }}
                    className={`flex items-center gap-2 px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                      selectedAuthorId === author.id ? "bg-gray-100" : ""
                    }`}
                  >
                    <img
                      loading="lazy"
                      src={
                        author.thumbnail ||
                        `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          author.name
                        )}&background=random&size=32`
                      }
                      alt={author.name}
                      className="w-6 h-6 rounded-full object-cover"
                      onError={(e) =>
                        (e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          author.name
                        )}&background=random&size=32`)
                      }
                    />
                    <span>{author.name}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        {/* Blog Reading Time block */}
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium mb-2">
              Reading Time (mins)
            </label>
            <input
              type="number"
              value={readingTime}
              min="0"
              max="59"
              onChange={(e) => {
                setReadingTime(+e.target.value);
              }}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[#18f7b1]"
              placeholder="Enter reading time"
            />
          </div>
          {/* Blog Date Time block */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Date and Time (EST/EDT)
            </label>
            <input
              type="datetime-local"
              value={dateTimeInEasternTime}
              onChange={(e) => {
                setDateTimeInEasternTime(e.target.value);
              }}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[#18f7b1]"
            />
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium mb-2">
              Article Category
            </label>
            <select
              onChange={handleBlogCategoryChange}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[#18f7b1]"
              value={selectedBlogCategoryId}
              defaultValue={1}
            >
              {blogCategories?.map((category) => {
                return (
                  <option
                    key={category.blog_category_id}
                    value={category.blog_category_id}
                  >
                    {category.blog_category_name}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Content</label>
          <MarkdownField
            value={content}
            onChange={(value) => setContent(value || "")}
            placeholder="Content for your article"
            minHeight={200}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Excerpt</label>
          <MarkdownField
            value={excerpt}
            onChange={(value) => setExcerpt(value || "")}
            placeholder="Include the Excerpt"
            minHeight={100}
            maxLength={250}
          />
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className={`
            px-6 py-3 bg-[#18f7b1] text-black rounded-lg hover:bg-[#0fd494] 
            flex items-center gap-2 transition-all duration-200
            ${isSaving ? "opacity-50 cursor-not-allowed" : ""}
          `}
        >
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
              Saving...
            </>
          ) : (
            <>
              <Save size={20} />
              {blogId ? "Update" : "Save"}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default Blog;
